import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { AdminLayout } from '@/components/layouts/admin-layout';
import FilterBar from '@/components/common/filter-bar';
import { toast } from 'sonner';
import { VirtualizedGrid } from '@/components/common/virtualized-grid';
import { AccessRequestCard } from '@/components/admin/access-request-card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

// Platform access request interface
export interface PlatformAccessRequest {
  id: number;
  email: string;
  name: string;
  message: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy: number | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  reviewer?: {
    id: number;
    name: string;
    email: string;
  } | null;
}

interface AccessRequestsData {
  requests: PlatformAccessRequest[];
  total: number;
  page: number;
  pageSize: number;
}

export default function AdminPlatformAccessRequestsPage() {
  const { isInitialized } = useAuthStore();
  const [requests, setRequests] = useState<PlatformAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const pageSize = 20;

  // Filter state managed by FilterBar
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    setPage(1);
    setHasMore(false);
  }, [filters]);

  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState<PlatformAccessRequest | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...filters
      });

      const response = await api.get<AccessRequestsData>(`/api/admin/platform-access-requests?${params}`);

      if (response.success && response.data) {
        const data = response.data;
        setRequests(prev => {
          if (page === 1) {
            return data.requests || [];
          }
          return [...prev, ...(data.requests || [])];
        });
        const fetchedCount = (data.requests || []).length;
        const serverTotal = data.total || 0;
        setTotal(serverTotal);
        setHasMore(fetchedCount === pageSize && page * pageSize < serverTotal);
        setTotalCount(serverTotal);
      } else {
        setError(response.error?.message || 'Failed to load access requests');
      }
    } catch (err) {
      console.error('Error fetching platform access requests:', err);
      setError('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const loadMore = () => {
    if (hasMore && !loading) {
      setPage(p => p + 1);
    }
  };

  useEffect(() => {
    if (!isInitialized) return;
    fetchRequests();
  }, [isInitialized, fetchRequests]);

  const handleApprove = async (request: PlatformAccessRequest) => {
    try {
      setProcessingId(request.id);

      const response = await api.put<{ message: string; request: PlatformAccessRequest }>(
        `/api/admin/platform-access-requests/${request.id}/approve`
      );

      if (response.success) {
        toast.success('Access request approved', {
          description: `${request.name} (${request.email}) can now sign up.`
        });
        // Update local state
        setRequests(prev =>
          prev.map(req =>
            req.id === request.id
              ? { ...req, status: 'approved', reviewedAt: new Date().toISOString() }
              : req
          )
        );
      } else {
        toast.error('Failed to approve request', {
          description: response.error?.message
        });
      }
    } catch (err) {
      console.error('Error approving request:', err);
      toast.error('Failed to approve request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    try {
      setProcessingId(selectedRequest.id);

      const response = await api.put<{ message: string; request: PlatformAccessRequest }>(
        `/api/admin/platform-access-requests/${selectedRequest.id}/reject`,
        { reason: rejectionReason || undefined }
      );

      if (response.success) {
        toast.success('Access request rejected', {
          description: `Request from ${selectedRequest.name} has been rejected.`
        });
        // Update local state
        setRequests(prev =>
          prev.map(req =>
            req.id === selectedRequest.id
              ? { ...req, status: 'rejected', reviewedAt: new Date().toISOString(), rejectionReason }
              : req
          )
        );
        setShowRejectDialog(false);
        setSelectedRequest(null);
        setRejectionReason('');
      } else {
        toast.error('Failed to reject request', {
          description: response.error?.message
        });
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      toast.error('Failed to reject request');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedRequest) return;

    try {
      setProcessingId(selectedRequest.id);

      const response = await api.delete<{ message: string }>(
        `/api/admin/platform-access-requests/${selectedRequest.id}`
      );

      if (response.success) {
        toast.success('Access request deleted', {
          description: `Request from ${selectedRequest.name} has been deleted.`
        });
        // Remove from local state
        setRequests(prev => prev.filter(req => req.id !== selectedRequest.id));
        setTotal(prev => prev - 1);
        setShowDeleteDialog(false);
        setSelectedRequest(null);
      } else {
        toast.error('Failed to delete request', {
          description: response.error?.message
        });
      }
    } catch (err) {
      console.error('Error deleting request:', err);
      toast.error('Failed to delete request');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (request: PlatformAccessRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const openDeleteDialog = (request: PlatformAccessRequest) => {
    setSelectedRequest(request);
    setShowDeleteDialog(true);
  };

  const openDetailsDialog = (request: PlatformAccessRequest) => {
    setSelectedRequest(request);
    setShowDetailsDialog(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      'pending': 'secondary',
      'approved': 'default',
      'rejected': 'destructive'
    };

    const icons = {
      'pending': <AlertCircle className="h-3 w-3" />,
      'approved': <CheckCircle className="h-3 w-3" />,
      'rejected': <XCircle className="h-3 w-3" />
    };

    return (
      <Badge variant={variants[status] || 'outline'} className="flex items-center gap-1">
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected').length;

  if (loading && requests.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Requests Grid */}
      <section className="space-y-3">
        <div className="px-4 text-sm text-muted-foreground sm:px-6">
          {totalCount} access requests
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex items-center gap-2">
            <FilterBar
            filters={[
              {
                id: 'search',
                label: 'Search',
                type: 'search',
                placeholder: 'Search by name, email, or message...'
              }
            ]}
            activeFilters={filters}
            onFilterChange={setFilters}
          />
            <Button variant="outline" size="icon" onClick={fetchRequests} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <div className="px-4 sm:px-6">
          <Tabs
            value={(filters.status as string) || 'all'}
            onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
          >
            <TabsList>
              <TabsTrigger value="all">All ({totalCount})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>
              <TabsTrigger value="approved">Approved ({approvedCount})</TabsTrigger>
              <TabsTrigger value="rejected">Rejected ({rejectedCount})</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="px-4 sm:px-6">
          {error ? (
            <div className="text-center py-8">
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Error Loading Requests</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={fetchRequests}>
                Try Again
              </Button>
            </div>
          ) : (
            <VirtualizedGrid
              items={requests}
              renderItem={(request) => (
                <AccessRequestCard
                  request={request}
                  onApprove={handleApprove}
                  onReject={openRejectDialog}
                  onViewDetails={openDetailsDialog}
                  onDelete={openDeleteDialog}
                  isProcessing={processingId === request.id}
                />
              )}
              keyExtractor={(request) => request.id.toString()}
              columns={{ sm: 1, md: 1, lg: 2, xl: 2 }}
              gap="gap-4"
              loading={loading}
              hasNextPage={hasMore}
              onLoadNextPage={loadMore}
              loadMoreThreshold={5}
              emptyMessage={
                (filters.search as string) || (filters.status as string) !== 'all'
                  ? 'No access requests match your search or filters'
                  : 'Platform access requests will appear here when users submit them'
              }
            />
          )}
        </div>
      </section>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Access Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject this access request from {selectedRequest?.name}?
              You can optionally provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason (optional)</Label>
              <Textarea
                id="rejection-reason"
                placeholder="Enter a reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={processingId === selectedRequest?.id}
            >
              {processingId === selectedRequest?.id ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : null}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Access Request</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this access request from {selectedRequest?.name}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={processingId === selectedRequest?.id}
            >
              {processingId === selectedRequest?.id ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : null}
              Delete Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Access Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{selectedRequest.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Submitted</Label>
                  <p className="text-sm">{formatDate(selectedRequest.createdAt)}</p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Message</Label>
                <p className="mt-1 text-sm bg-muted p-3 rounded-md whitespace-pre-wrap">
                  {selectedRequest.message}
                </p>
              </div>

              {selectedRequest.reviewedAt && (
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-muted-foreground">Reviewed At</Label>
                    <p className="text-sm">{formatDate(selectedRequest.reviewedAt)}</p>
                  </div>
                  {selectedRequest.reviewer && (
                    <div>
                      <Label className="text-muted-foreground">Reviewed By</Label>
                      <p className="text-sm">{selectedRequest.reviewer.name}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedRequest.rejectionReason && (
                <div>
                  <Label className="text-muted-foreground">Rejection Reason</Label>
                  <p className="mt-1 text-sm bg-destructive/10 text-destructive p-3 rounded-md">
                    {selectedRequest.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    openRejectDialog(selectedRequest);
                  }}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailsDialog(false);
                    handleApprove(selectedRequest);
                  }}
                  disabled={processingId === selectedRequest?.id}
                >
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
